import { useEffect, useRef, useState } from 'react'

import Route from '../../../app/Route.enum'
import { useAnalytics } from '../../../modules/analytics'
import { useSavedMoveId } from '../../../modules/moves'
import { useNavigate } from '../../../modules/navigation'
import FullWidthPage from '../../../components/layout/FullWidthPage'
import FormCarousel from '../../../components/FormCarousel'
import Spinner from '../../../components/Spinner'
import DateTimeStep from './DateTimeStep'
import AddressesStep from './AddressesStep'
import MoveSizeStep from './MoveSizeStep'
import { type QuotesRequestFormValues } from './QuotesRequestPage.type'
import QuotesPreviewPage from '../QuotesPreviewPage'
import ProgressBarDescription from './ProgressBarDescription'
import ExitModal from './ExitModal'
import { useDefaultValues } from './useDefaultValues'

const QuotesRequestPage: React.FC = () => {
  const started = useRef(false)
  const { pushCustomPageView, pushStartQuotesRequestEvent } = useAnalytics()
  const [quotesRequest, setQuotesRequest] = useState<QuotesRequestFormValues>()
  const { navigate } = useNavigate()
  const moveId = useSavedMoveId()
  const hasMove = !!moveId
  const { ready, defaultValues } = useDefaultValues({
    skip: hasMove || !!quotesRequest,
  })

  const [currentSlide, setCurrentSlide] = useState(0)

  if (!hasMove && !started.current && ready) {
    started.current = true
    pushStartQuotesRequestEvent()
  }

  /**
   * redirects user to his move if any
   */
  useEffect(() => {
    if (hasMove) {
      navigate(Route.HomePage)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasMove])

  if (hasMove) {
    return null
  }

  /**
   * push tracking event
   */
  const onSlideChange = (step: string, index: number) => {
    setCurrentSlide(index)
    pushCustomPageView(`${Route.QuotesRequest}/${step}`)
  }

  if (quotesRequest) {
    return (
      <QuotesPreviewPage quotesRequest={quotesRequest} />
    )
  }

  if (!ready) {
    return (
      <FullWidthPage>
        <Spinner floating />
      </FullWidthPage>
    )
  }

  const slides = {
    addresses: <AddressesStep data={defaultValues?.addresses} />,
    dateTime: <DateTimeStep data={defaultValues?.dateTime} />,
    moveSize: <MoveSizeStep />,
  }
  const nbSlides = Object.keys(slides).length

  const getProgress = () => {
    const progress = (currentSlide / (nbSlides - 1)) * 100
    return Math.max(progress - 10, 5)
  }

  return (
    <FullWidthPage>
      <ExitModal />
      <FormCarousel<QuotesRequestFormValues>
        onSubmit={setQuotesRequest}
        slides={slides}
        contentWidth={1300}
        onSlideChange={onSlideChange}
        progressBarProps={{
          progress: getProgress(),
          nbDots: nbSlides,
        }}
        progressBarDescription={<ProgressBarDescription />}
      />

    </FullWidthPage>
  )
}

export default QuotesRequestPage
